// themeStore — persistent theme preference (plan §P2.18 — UI Workflow
// Consistency Audit + Carbon expansion).
//
// Three modes are supported:
//
//   * ``dark``   — UX v0.7 dark baseline. Activates the ``.dark`` class
//     on ``<html>`` so Tailwind's ``dark:`` variants resolve and the
//     shadcn dark token block in ``index.css`` takes effect.
//   * ``light``  — shadcn light token block (no class on ``<html>``)
//     plus the ``html:not(.dark):not(.carbon)`` overrides in
//     ``index.css`` that re-target hard-coded slate utilities and
//     ``.alm-*`` primitives.
//   * ``carbon`` — Tuttify Carbon editorial theme (bone background,
//     molten red accent, Geist / Instrument Serif typography). The
//     ``.carbon`` class on ``<html>`` switches the CSS variables in
//     ``index.css`` into the Tuttify palette.
//
// The choice is persisted to localStorage so it survives reloads. On
// first visit we honour ``prefers-color-scheme`` (no carbon default —
// the user has to explicitly opt in).

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'dark' | 'light' | 'carbon'

/** Canonical cycle order — used by the Layout button. */
export const THEME_CYCLE: readonly ThemeMode[] = ['dark', 'light', 'carbon'] as const

interface ThemeStore {
  theme: ThemeMode
  setTheme: (mode: ThemeMode) => void
  /** Advance to the next theme in ``THEME_CYCLE``. */
  cycle: () => void
  /**
   * Back-compat alias for the old two-mode toggle. Now an alias for
   * ``cycle`` so existing keyboard-shortcut wiring keeps working.
   */
  toggle: () => void
}

function defaultTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'dark'
  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light'
  }
  return 'dark'
}

function nextTheme(current: ThemeMode): ThemeMode {
  const idx = THEME_CYCLE.indexOf(current)
  if (idx === -1) return THEME_CYCLE[0]!
  return THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]!
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: defaultTheme(),
      setTheme: (theme) => set({ theme }),
      cycle: () => set({ theme: nextTheme(get().theme) }),
      toggle: () => set({ theme: nextTheme(get().theme) }),
    }),
    { name: 'ai_alm_theme_v07' },
  ),
)

/**
 * Applies the current theme to the ``<html>`` element by setting the
 * appropriate class. Call this once during app boot (and on every
 * theme change) so Tailwind's ``dark:`` variants and our scoped
 * ``html.carbon`` / ``html:not(.dark)`` overrides resolve correctly.
 *
 * The function is defensive: it strips any other theme classes before
 * adding the new one, so a stale ``dark`` / ``carbon`` class from a
 * previous render cannot stack on top of the current selection.
 */
export function applyTheme(theme: ThemeMode): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.remove('dark', 'carbon')
  if (theme === 'dark') root.classList.add('dark')
  else if (theme === 'carbon') root.classList.add('carbon')
  // ``light`` has no class — the default ``:root`` palette applies.
}
